const CATEGORY_LEVELS = [1, 2, 3];

/**
 * Build a SearchPage query string from listing type + cumulative category levels.
 *
 * @param {string|undefined} listingType
 * @param {Array<{ level: number, id: string }>} levels
 * @returns {string} query string including leading `?`, or empty string when no params
 */
const buildSearch = (listingType, levels) => {
  const params = new URLSearchParams();
  if (listingType) {
    params.set('pub_listingType', listingType);
  }
  levels.forEach(({ level, id }) => {
    params.set(`pub_categoryLevel${level}`, id);
  });
  const query = params.toString();
  return query ? `?${query}` : '';
};

/**
 * Build category breadcrumb crumbs for a listing.
 * Walks categoryLevel1 → 3 along the Console category tree path. The first missing
 * id, unknown id, or id that is not a child of the previous level stops the trail.
 * That keeps Search URLs contiguous and valid for SearchPage nested-category parsing.
 * Returns [] when nothing resolves.
 *
 * @param {Object} publicData - listing publicData
 * @param {Object} categoryConfiguration - config.categoryConfiguration
 * @returns {Array<{ id: string, label: string, search: string }>}
 */
export const buildCategoryBreadcrumbs = (publicData, categoryConfiguration) => {
  let nodesAtLevel = categoryConfiguration?.categories || [];
  const listingType = publicData?.listingType;
  const resolved = [];

  for (const level of CATEGORY_LEVELS) {
    const id = publicData?.[`categoryLevel${level}`];
    if (!id || !nodesAtLevel.length) {
      break;
    }
    const match = nodesAtLevel.find(category => category.id === id);
    if (!match?.name) {
      break;
    }
    resolved.push({ level, id, label: match.name });
    nodesAtLevel = match.subcategories || [];
  }

  return resolved.map((crumb, index) => {
    const levelsThroughHere = resolved.slice(0, index + 1).map(({ level, id }) => ({ level, id }));
    return {
      id: crumb.id,
      label: crumb.label,
      search: buildSearch(listingType, levelsThroughHere),
    };
  });
};
